// A scoped signal that lets a user-initiated skip gesture (transport /
// media-session next-previous) tell the metrics binding that the `current`
// change it causes is a deliberate skip, not a natural `ended`→advance.
//
// WHY a module-level signal rather than a param threaded through the
// controller: `skipNext()`/`skipPrevious()` are shared by the natural
// `ended` auto-advance and by user gestures, so the controller itself can't
// tell them apart. Stamping intent at the gesture callsite and reading it at
// the metrics seam keeps the transport path untouched and the metric purely
// observational.

import { globalRef } from '@amazon/vinyl-util'
import { markInteractionStart } from '@/perf/playbackDelayMarks'

export type SkipDirection = 'next' | 'previous'

const userSkipRef = globalRef<{ pending: { readonly direction: SkipDirection } | null }>(() => ({
    pending: null,
}))

/**
 * Runs a user skip with its direction marked until the skip settles, so the
 * `current` change it makes — after however many queue reads it awaits — is
 * attributed to the user. A skip at the queue boundary that changes nothing
 * releases the mark unread, so a later machine advance can't inherit it.
 *
 * Every user-triggered skip (transport next/previous buttons + swipe, keyboard,
 * media keys, media-session, audiobook chapter nav, overflow "skip to previous")
 * routes through here; the natural `ended`→advance and autoplay continuation do
 * NOT. A user skip starts a NEW track, so this also opens the perf journey
 * window: its interaction→playing bar closes at the imminent track's `playing`
 * (a skip reuses the live queue and never reaches markPlayStart, so this is the
 * window's only opener).
 */
export async function withUserSkip<T>(
    direction: SkipDirection,
    skip: () => T | Promise<T>
): Promise<T> {
    const userSkip = userSkipRef.value
    const mark = { direction }
    userSkip.pending = mark
    markInteractionStart()
    try {
        return await skip()
    } finally {
        if (userSkip.pending === mark) userSkip.pending = null
    }
}

/** Reads and clears the pending direction; null when the advance wasn't a user skip. */
export function consumeUserSkip(): SkipDirection | null {
    const userSkip = userSkipRef.value
    const direction = userSkip.pending?.direction ?? null
    userSkip.pending = null
    return direction
}
